import React, { createContext, useContext, useEffect, useState, PropsWithChildren, FC } from 'react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'

import { getFullName } from 'selectors/user/session'
import { getPagePath } from 'selectors/page'
import { STUDIO_HASH_CHANGE_EVENT } from 'lib/studio/navigate'

import { NAV_SECTIONS, isActiveNavItem } from './helpers/constants'

const getActiveSectionTitle = (activeHref: string): string | null => {
  const activeSection = NAV_SECTIONS.find((section) =>
    section.items.some((item) => item.href !== '' && isActiveNavItem(activeHref, item))
  )
  return activeSection?.title ?? null
}

export type NavigationContextValue = {
  fullName: string
  activeHref: string
  isMobileNavOpen: boolean
  onToggleMobileNav: () => void
  onCloseMobileNav: () => void
  isDesktopCollapsed: boolean
  onToggleDesktopCollapse: () => void
  openSectionTitle: string | null
  onToggleSection: (title: string) => void
}

const NavigationContext = createContext<NavigationContextValue | null>(null)

export const NavigationProvider: FC<PropsWithChildren> = ({ children }) => {
  const fullName = useSelector(getFullName)
  const pagePath = useSelector(getPagePath)
  const router = useRouter()
  const [hash, setHash] = useState('')

  useEffect(() => {
    const updateHash = () => setHash(window.location.hash)
    updateHash()
    router.events.on('routeChangeComplete', updateHash)
    router.events.on('hashChangeComplete', updateHash)
    window.addEventListener(STUDIO_HASH_CHANGE_EVENT, updateHash)
    return () => {
      router.events.off('routeChangeComplete', updateHash)
      router.events.off('hashChangeComplete', updateHash)
      window.removeEventListener(STUDIO_HASH_CHANGE_EVENT, updateHash)
    }
  }, [router.events])

  const activeHref = `${pagePath}${hash}`

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false)
  const [openSectionTitle, setOpenSectionTitle] = useState<string | null>(() =>
    getActiveSectionTitle(activeHref)
  )

  useEffect(() => {
    setOpenSectionTitle(getActiveSectionTitle(activeHref))
    if (!hash.includes('id-verification')) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }
  }, [activeHref, hash])

  const value: NavigationContextValue = {
    fullName,
    activeHref,
    isMobileNavOpen,
    onToggleMobileNav: () => setIsMobileNavOpen((prevValue) => !prevValue),
    onCloseMobileNav: () => setIsMobileNavOpen(false),
    isDesktopCollapsed,
    onToggleDesktopCollapse: () => setIsDesktopCollapsed((prevValue) => !prevValue),
    openSectionTitle,
    onToggleSection: (title) =>
      setOpenSectionTitle((prevTitle) => (prevTitle === title ? null : title)),
  }

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>
}

export const useNavigation = (): NavigationContextValue => {
  const context = useContext(NavigationContext)
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider')
  }
  return context
}
